//row-column-insert.js

import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

export const insertKey = new PluginKey("tableRowColumnInsert");

const BUTTON_SIZE = 20;
const ROW_GUTTER_WIDTH = 50; // Width of the left gutter area for row detection
const COL_GUTTER_HEIGHT = 50; // Height of the top gutter area for column detection

/**
 * Creates the row/column insert visual cues plugin for ProseMirror tables.
 * Shows "+" buttons and highlight lines when hovering near row/column boundaries.
 */
export function rowColumnInsert({ editor, isEditable = true } = {}) {
    // Disable the column insert plugin due to UI issues. Will re-enable in the
    // future when we address the pending issues
    // eslint-disable-next-line no-constant-condition
    if (!isEditable || true) {
        return new Plugin({ key: insertKey });
    }

    let plugin = new Plugin({
        key: insertKey,
        state: {
            init() {
                return new InsertState(null, null, null);
            },
            apply(tr, prev) {
                return prev.apply(tr);
            },
        },
        props: {
            handleDOMEvents: {
                mousemove(view, event) {
                    handleMouseMove(view, event, editor);
                    return false;
                },
                mouseleave(view, event) {
                    // Delay clearing to allow clicking on buttons
                    setTimeout(() => {
                        const pluginState = insertKey.getState(view.state);
                        if (pluginState && (pluginState.rowHandle || pluginState.colHandle)) {
                            // Check if mouse is over a button before clearing
                            const buttons = document.querySelectorAll(
                                ".row-insert-button, .col-insert-button",
                            );
                            for (const button of buttons) {
                                if (button.matches(":hover")) {
                                    return;
                                }
                            }
                            clearHandles(view);
                        }
                    }, 100);
                    return false;
                },
            },
            decorations(state) {
                let pluginState = insertKey.getState(state);
                if (pluginState && (pluginState.rowHandle || pluginState.colHandle)) {
                    return createDecorations(state, pluginState, editor);
                }
                return DecorationSet.empty;
            },
        },
    });

    return plugin;
}

/**
 * State class for tracking active insert handles
 */
class InsertState {
    constructor(rowHandle, colHandle, tableInfo) {
        this.rowHandle = rowHandle;
        this.colHandle = colHandle;
        this.tableInfo = tableInfo;
    }

    apply(tr) {
        let action = tr.getMeta(insertKey);
        if (action && action.setRowHandle !== undefined) {
            return new InsertState(
                action.setRowHandle,
                action.setColHandle !== undefined ? action.setColHandle : this.colHandle,
                action.tableInfo || this.tableInfo,
            );
        }
        if (action && action.setColHandle !== undefined) {
            return new InsertState(
                action.setRowHandle !== undefined ? action.setRowHandle : this.rowHandle,
                action.setColHandle,
                action.tableInfo || this.tableInfo,
            );
        }
        if (action && action.clear) {
            return new InsertState(null, null, null);
        }
        if (tr.docChanged) {
            return new InsertState(null, null, null);
        }
        return this;
    }
}

/**
 * Find the nearest table to the mouse position
 */
function findNearestTable(view, clientX, clientY) {
    const tables = view.dom.querySelectorAll("table");

    for (const table of tables) {
        const rect = table.getBoundingClientRect();

        // Check if mouse is in the row gutter (left side of table)
        const isInRowGutter =
            clientX >= rect.left - ROW_GUTTER_WIDTH &&
            clientX <= rect.left + 20 &&
            clientY >= rect.top &&
            clientY <= rect.bottom;

        // Check if mouse is in the column gutter (top side of table)
        const isInColGutter =
            clientY >= rect.top - COL_GUTTER_HEIGHT &&
            clientY <= rect.top + 20 &&
            clientX >= rect.left &&
            clientX <= rect.right;

        // Check if mouse is inside the table
        const isInsideTable =
            clientX >= rect.left &&
            clientX <= rect.right &&
            clientY >= rect.top &&
            clientY <= rect.bottom;

        if (isInRowGutter || isInColGutter || isInsideTable) {
            return {
                element: table,
                rect: rect,
                width: rect.width,
                height: rect.height,
                top: rect.top,
                left: rect.left,
                right: rect.right,
                bottom: rect.bottom,
                isInRowGutter,
                isInColGutter,
            };
        }
    }

    return null;
}

/**
 * Find the closest row edge to the mouse Y position
 */
function findClosestRowEdge(tableEl, clientY) {
    const rows = tableEl.querySelectorAll("tr");
    let closestEdge = null;
    let minDistance = Infinity;

    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const rect = row.getBoundingClientRect();

        // Check top edge
        const topDistance = Math.abs(clientY - rect.top);
        if (topDistance < minDistance) {
            minDistance = topDistance;
            closestEdge = {
                rowIndex: i,
                position: "before",
                y: rect.top,
                rowElement: row,
            };
        }

        // Check bottom edge (don't add after footnote rows)
        if (!row.classList.contains("table-footnote")) {
            const bottomDistance = Math.abs(clientY - rect.bottom);
            if (bottomDistance < minDistance) {
                minDistance = bottomDistance;
                closestEdge = {
                    rowIndex: i + 1,
                    position: "after",
                    y: rect.bottom,
                    rowElement: row,
                };
            }
        }
    }

    return closestEdge;
}

/**
 * Find the closest column edge to the mouse X position
 */
function findClosestColEdge(tableEl, clientX) {
    const firstRow = tableEl.querySelector("tr");
    if (!firstRow) {
        return null;
    }

    const cells = firstRow.querySelectorAll("th, td");
    let closestEdge = null;
    let minDistance = Infinity;

    for (let i = 0; i < cells.length; i++) {
        const cell = cells[i];
        const rect = cell.getBoundingClientRect();

        // Check left edge
        const leftDistance = Math.abs(clientX - rect.left);
        if (leftDistance < minDistance) {
            minDistance = leftDistance;
            closestEdge = {
                colIndex: i,
                position: "before",
                x: rect.left,
                cellElement: cell,
            };
        }

        // Check right edge
        const rightDistance = Math.abs(clientX - rect.right);
        if (rightDistance < minDistance) {
            minDistance = rightDistance;
            closestEdge = {
                colIndex: i + 1,
                position: "after",
                x: rect.right,
                cellElement: cell,
            };
        }
    }

    return closestEdge;
}

/**
 * Get the ProseMirror table position from a DOM table element
 */
function getTablePosFromDOM(view, tableEl) {
    let wrapper = tableEl.parentNode;
    while (wrapper && !wrapper.classList?.contains("tableWrapper")) {
        wrapper = wrapper.parentNode;
    }

    if (!wrapper) {
        return null;
    }

    const tableId = wrapper.getAttribute("data-table-id");
    if (!tableId) {
        return null;
    }

    let tablePos = null;
    let tableStart = null;
    let tableNode = null;

    view.state.doc.descendants((node, pos) => {
        if (node.type.name === "table" && node.attrs.id === tableId) {
            tablePos = pos;
            tableStart = pos + 1;
            tableNode = node;
            return false;
        }
    });

    return { tablePos, tableStart, tableNode };
}

/**
 * Handle mouse move events
 */
function handleMouseMove(view, event, editor) {
    const { clientX, clientY } = event;
    const target = event.target;

    // Don't change state if hovering over buttons
    if (
        target.closest?.(".row-insert-container") ||
        target.closest?.(".col-insert-container") ||
        target.closest?.(".row-insert-button") ||
        target.closest?.(".col-insert-button")
    ) {
        return;
    }

    const tableInfo = findNearestTable(view, clientX, clientY);

    if (!tableInfo) {
        clearHandles(view);
        return;
    }

    const tableEl = tableInfo.element;
    const tablePosInfo = getTablePosFromDOM(view, tableEl);

    if (!tablePosInfo) {
        clearHandles(view);
        return;
    }

    let rowHandle = null;
    let colHandle = null;

    // Only show row insert when in the left gutter area
    if (tableInfo.isInRowGutter) {
        rowHandle = findClosestRowEdge(tableEl, clientY);
        if (rowHandle) {
            rowHandle = { ...rowHandle, ...tablePosInfo };
        }
    }

    // Only show column insert when in the top gutter area
    if (tableInfo.isInColGutter) {
        colHandle = findClosestColEdge(tableEl, clientX);
        if (colHandle) {
            colHandle = { ...colHandle, ...tablePosInfo };
        }
    }

    if (rowHandle || colHandle) {
        view.dispatch(
            view.state.tr.setMeta(insertKey, {
                setRowHandle: rowHandle,
                setColHandle: colHandle,
                tableInfo: {
                    ...tableInfo,
                    tableNode: tablePosInfo.tableNode,
                },
            }),
        );
    } else {
        clearHandles(view);
    }
}

/**
 * Clear all insert handles
 */
function clearHandles(view) {
    const pluginState = insertKey.getState(view.state);
    if (pluginState && (pluginState.rowHandle || pluginState.colHandle)) {
        view.dispatch(view.state.tr.setMeta(insertKey, { clear: true }));
    }
}

/**
 * Insert a row at the specified position
 */
function insertRow(editor, rowHandle) {
    const { tableNode, tableStart, rowIndex, position } = rowHandle;

    if (!tableNode) {
        return;
    }

    const targetRowIndex = position === "before" ? rowIndex : Math.max(0, rowIndex - 1);
    const row = tableNode.content.child(Math.min(targetRowIndex, tableNode.content.childCount - 1));

    if (!row) {
        return;
    }

    let cellPos = tableStart;
    for (let i = 0; i < targetRowIndex && i < tableNode.content.childCount; i++) {
        cellPos += tableNode.content.child(i).nodeSize;
    }
    cellPos += 1;

    try {
        if (position === "before" || rowIndex === 0) {
            editor.chain().focus(cellPos).addRowBefore().run();
        } else {
            editor.chain().focus(cellPos).addRowAfter().run();
        }
    } catch (e) {
        console.warn("Error inserting row:", e);
    }
}

/**
 * Insert a column at the specified position
 * Uses custom insertTableColumnBefore/After commands that properly
 * redistribute column widths to maintain table width within the document.
 */
function insertColumn(editor, colHandle) {
    const { tableNode, tableStart, colIndex, position } = colHandle;

    if (!tableNode) {
        return;
    }

    const firstRow = tableNode.content.child(0);
    if (!firstRow) {
        return;
    }

    let cellPos = tableStart + 1;
    const targetColIndex = position === "before" ? colIndex : Math.max(0, colIndex - 1);

    for (let i = 0; i < targetColIndex && i < firstRow.content.childCount; i++) {
        cellPos += firstRow.content.child(i).nodeSize;
    }

    try {
        // Use custom commands that redistribute column widths to maintain table width
        if (position === "before") {
            editor.chain().focus(cellPos).insertTableColumnBefore().run();
        } else {
            editor.chain().focus(cellPos).insertTableColumnAfter().run();
        }
    } catch (e) {
        console.warn("Error inserting column:", e);
    }
}

/**
 * Create decorations for insert buttons and highlight lines
 */
function createDecorations(state, pluginState, editor) {
    const decorations = [];
    const { rowHandle, colHandle, tableInfo } = pluginState;

    if (rowHandle && tableInfo) {
        const buttonWidget = createRowInsertButton(rowHandle, tableInfo, editor);

        if (rowHandle.tablePos !== null) {
            decorations.push(
                Decoration.widget(rowHandle.tablePos, buttonWidget, {
                    side: -1,
                    key: `row-insert-${rowHandle.rowIndex}-${rowHandle.y}`,
                }),
            );
        }
    }

    if (colHandle && tableInfo) {
        const buttonWidget = createColInsertButton(colHandle, tableInfo, editor);

        if (colHandle.tablePos !== null) {
            decorations.push(
                Decoration.widget(colHandle.tablePos, buttonWidget, {
                    side: -1,
                    key: `col-insert-${colHandle.colIndex}-${colHandle.x}`,
                }),
            );
        }
    }

    return DecorationSet.create(state.doc, decorations);
}

/**
 * Create the row insert button DOM element
 */
function createRowInsertButton(rowHandle, tableInfo, editor) {
    const container = document.createElement("div");
    container.className = "row-insert-container";
    container.style.cssText = `
        position: fixed;
        left: ${tableInfo.left - BUTTON_SIZE - 10}px;
        top: ${rowHandle.y - BUTTON_SIZE / 2}px;
        z-index: 1000;
        pointer-events: auto;
    `;

    const button = document.createElement("div");
    button.className = "row-insert-button";
    button.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
    `;
    button.title = "Insert row";

    // Direct click handler on the button
    button.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        insertRow(editor, rowHandle);
    });

    button.addEventListener("mousedown", (e) => {
        e.preventDefault();
        e.stopPropagation();
    });

    const line = document.createElement("div");
    line.className = "row-insert-line";
    line.style.cssText = `
        position: fixed;
        left: ${tableInfo.left}px;
        top: ${rowHandle.y - 1}px;
        width: ${tableInfo.width}px;
        height: 3px;
        background-color: #4a90d9;
        pointer-events: none;
        z-index: 999;
    `;

    container.appendChild(button);
    container.appendChild(line);

    return container;
}

/**
 * Create the column insert button DOM element
 */
function createColInsertButton(colHandle, tableInfo, editor) {
    const container = document.createElement("div");
    container.className = "col-insert-container";
    container.style.cssText = `
        position: fixed;
        left: ${colHandle.x - BUTTON_SIZE / 2}px;
        top: ${tableInfo.top - BUTTON_SIZE - 10}px;
        z-index: 1000;
        pointer-events: auto;
    `;

    const button = document.createElement("div");
    button.className = "col-insert-button";
    button.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
    `;
    button.title = "Insert column";

    // Direct click handler on the button
    button.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        insertColumn(editor, colHandle);
    });

    button.addEventListener("mousedown", (e) => {
        e.preventDefault();
        e.stopPropagation();
    });

    const line = document.createElement("div");
    line.className = "col-insert-line";
    line.style.cssText = `
        position: fixed;
        left: ${colHandle.x - 1}px;
        top: ${tableInfo.top}px;
        width: 3px;
        height: ${tableInfo.height}px;
        background-color: #4a90d9;
        pointer-events: none;
        z-index: 999;
    `;

    container.appendChild(button);
    container.appendChild(line);

    return container;
}

export default rowColumnInsert;
